import React, { useState, useEffect, useCallback } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { testimonials } from '../data/mock';

const AUTO_ROTATE_INTERVAL = 5000; // 5 seconds

// Google "G" Logo SVG component
const GoogleLogo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

// Generate initials avatar background color based on name
const getAvatarColor = (name) => {
  const colors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 
    'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const getInitials = (name) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Format relative time like Google
const getRelativeTime = (index) => {
  const times = ['5 months ago', '6 months ago', '9 months ago', '10 months ago', '11 months ago', '1 year ago'];
  return times[index % times.length];
};

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextTestimonial = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, []);

  const prevTestimonial = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  // Auto-rotation effect
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      nextTestimonial();
    }, AUTO_ROTATE_INTERVAL);

    return () => clearInterval(interval);
  }, [isPaused, nextTestimonial]);

  const currentTestimonial = testimonials[currentIndex];
  const isGoogleReview = currentTestimonial.source === 'Google Review';

  return (
    <section id="testimonials" className="py-24 bg-gray-50 relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        {/* Header with Google Rating */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <GoogleLogo className="w-10 h-10" />
            <span className="text-2xl font-medium text-gray-700">Reviews</span>
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-5xl font-medium text-gray-900">4.4</span>
            <div className="flex flex-col items-start">
              <div className="flex">
                {[1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-5 w-5 text-yellow-400 fill-yellow-400" />
                ))}
                <Star className="h-5 w-5 text-yellow-400 fill-yellow-400 before-image" />
              </div>
              <span className="text-sm text-gray-500">Based on 14 reviews</span>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card 
            className="border border-gray-200 shadow-sm bg-white rounded-lg"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            data-testid="testimonial-card"
          >
            <CardContent className="p-6">
              {/* Google Review Header */}
              <div className="flex items-start gap-4 mb-4">
                {/* Avatar */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm ${getAvatarColor(currentTestimonial.name)}`}>
                  {getInitials(currentTestimonial.name)}
                </div>
                
                {/* Name and metadata */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900" data-testid="testimonial-name">
                      {currentTestimonial.name}
                    </h4>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <span>{getRelativeTime(currentIndex)}</span>
                  </div>
                  {/* Star Rating */}
                  <div className="flex items-center gap-1 mt-1">
                    {[...Array(currentTestimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                </div>

                {/* Google Logo */}
                {isGoogleReview && (
                  <GoogleLogo className="w-6 h-6 flex-shrink-0" />
                )}
              </div>

              {/* Review Text */}
              <p 
                className="text-gray-700 text-base leading-relaxed"
                data-testid="testimonial-text"
              >
                {currentTestimonial.text}
              </p>

              {/* Source badge for non-Google reviews */}
              {!isGoogleReview && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded" data-testid="testimonial-source">
                    {currentTestimonial.source}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Navigation */}
          <div 
            className="flex justify-center items-center gap-4 mt-6"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <Button
              onClick={prevTestimonial}
              variant="outline"
              size="icon"
              className="rounded-full w-10 h-10 border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              data-testid="testimonial-prev-btn"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>

            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  data-testid={`testimonial-dot-${index}`}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex
                      ? 'bg-[#E63946] w-6'
                      : 'bg-gray-300 hover:bg-gray-400 w-2'
                  }`}
                />
              ))}
            </div>

            <Button
              onClick={nextTestimonial}
              variant="outline"
              size="icon"
              className="rounded-full w-10 h-10 border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              data-testid="testimonial-next-btn"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* View All Reviews Link */}
          <div className="text-center mt-6">
            <a 
              href="https://share.google/WEOeKPXdMHTQLb9RG" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#E63946] hover:text-[#D32F3D] text-sm font-medium inline-flex items-center gap-1"
              data-testid="view-all-reviews-link"
            >
              View all Google reviews
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
